import { useState } from "react";
import { toast } from "sonner";
import { Copy, ExternalLink, KeyRound, RefreshCw, Eye, Ban } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../ui/dialog";
import { Button } from "../ui/button";
import api, { apiError } from "../../lib/api";
import { deckUrl, previewUrl } from "../../lib/deckModel";

const TYPES = [
  ["draft", "Draft", "Only ARQITEK users can view."],
  ["private", "Private Link", "Anyone with the direct URL can view."],
  ["password", "Password Protected", "Viewer must enter the password."],
  ["public", "Public", "Anyone can view."],
];
const fmtDate = (d) => (d ? new Date(d).toLocaleString() : "—");
const copy = (t, msg) => navigator.clipboard.writeText(t).then(() => toast.success(msg));

export function AccessDialog({ deck, open, onOpenChange, onSaved }) {
  const [type, setType] = useState(deck?.access?.type || "draft");
  const [pw, setPw] = useState(deck?.access?.password || "");
  const [busy, setBusy] = useState(false);
  if (!deck) return null;
  const save = async (extra = {}) => {
    setBusy(true);
    try {
      const { data } = await api.put(`/decks/${deck.id}/access`, { type, password: pw, ...extra });
      setType(data.access.type); setPw(data.access.password || "");
      onSaved?.(data); toast.success("Access updated");
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  const url = deckUrl(deck.slug);
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="deck-access-dialog">
        <DialogHeader>
          <DialogTitle>Access &amp; Sharing</DialogTitle>
          <DialogDescription>{deck.name}</DialogDescription>
        </DialogHeader>
        <div className="space-y-5">
          <div className="rounded-lg border border-border p-3">
            <div className="text-[11px] uppercase tracking-wide text-muted-foreground">Public URL</div>
            <div className="mt-1 flex items-center gap-2">
              <span className="font-mono text-sm truncate flex-1" data-testid="deck-access-url">{url.replace("https://", "")}</span>
              <Button size="sm" variant="outline" onClick={() => copy(url, "Link copied")} data-testid="deck-access-copy-link"><Copy className="h-3.5 w-3.5 mr-1" />Copy</Button>
              <Button size="sm" variant="outline" onClick={() => window.open(previewUrl(deck.slug), "_blank")} data-testid="deck-access-preview-visitor"><Eye className="h-3.5 w-3.5 mr-1" />Visitor view</Button>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-2">
            {TYPES.map(([k, l, d]) => (
              <button key={k} type="button" onClick={() => setType(k)} data-testid={`deck-access-type-${k}`}
                className={`text-left rounded-lg border p-3 transition-colors ${type === k ? "border-primary bg-primary/10" : "border-border hover:bg-secondary/40"}`}>
                <div className="text-sm font-medium">{l}</div><div className="text-[11px] text-muted-foreground mt-0.5">{d}</div>
              </button>
            ))}
          </div>
          {type === "password" && (
            <div className="space-y-2">
              <label className="text-[11px] uppercase tracking-wide text-muted-foreground">Password</label>
              <div className="flex gap-2">
                <input value={pw} onChange={(e) => setPw(e.target.value)} className="flex-1 rounded-md border border-border bg-secondary/30 px-3 py-2 text-sm font-mono" data-testid="deck-access-password" />
                <Button size="sm" variant="outline" onClick={() => copy(pw, "Password copied")} disabled={!pw} data-testid="deck-access-copy-password"><Copy className="h-3.5 w-3.5" /></Button>
                <Button size="sm" variant="outline" onClick={() => save({ regenerate: true })} disabled={busy} data-testid="deck-access-regenerate"><RefreshCw className="h-3.5 w-3.5" /></Button>
              </div>
            </div>
          )}
          <div className="grid grid-cols-2 gap-2 text-xs text-muted-foreground">
            <div>Access type: <span className="text-foreground" data-testid="deck-access-current">{TYPES.find((t) => t[0] === deck.access?.type)?.[1]}</span></div>
            <div>Published: <span className="text-foreground">{fmtDate(deck.published_at)}</span></div>
            <div>Last updated: <span className="text-foreground">{fmtDate(deck.updated_at)}</span></div>
            <div>Revision: <span className="text-foreground">{deck.revision}</span></div>
          </div>
          <div className="flex justify-between gap-2">
            <Button variant="ghost" className="text-destructive" onClick={() => { setType("draft"); save({ type: "draft" }); }} disabled={busy} data-testid="deck-access-disable"><Ban className="h-4 w-4 mr-1.5" />Disable access</Button>
            <div className="flex gap-2">
              {type === "password" && deck.access?.password && <Button variant="outline" onClick={() => { setPw(""); setType("private"); save({ type: "private", password: "" }); }} disabled={busy} data-testid="deck-access-remove-password"><KeyRound className="h-4 w-4 mr-1.5" />Remove password</Button>}
              <Button onClick={() => save()} disabled={busy} data-testid="deck-access-save"><ExternalLink className="h-4 w-4 mr-1.5" />Save access</Button>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
